<template>
  <div class="tenant-user-management-container">
    <a-card title="租户用户管理" :bordered="false">
      <div slot="extra">
        <a-button type="primary" @click="openCreateDialog">新增用户</a-button>
        <a-button style="margin-left: 10px;" @click="loadUserData">刷新</a-button>
      </div>

      <a-form layout="inline" :form="searchFormRef">
        <!-- 超管可见的组织选择（支持搜索过滤） -->
        <a-form-item label="组织" v-if="isSuperAdmin">
          <a-select
            v-model="searchForm.tenant_id"
            placeholder="输入组织名称搜索"
            style="width: 250px;"
            allowClear
            show-search
            :filter-option="filterTenantOption"
            @change="handleTenantChange"
            :dropdownMatchSelectWidth="false"
            :showArrow="true"
          >
            <a-select-option
              v-for="tenant in tenants"
              :key="tenant.id"
              :value="tenant.id">
              {{ tenant.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        
        <a-form-item label="用户名">
          <a-input v-model="searchForm.user_name" placeholder="请输入用户名" allowClear />
        </a-form-item>
        <a-form-item label="手机号">
          <a-input v-model="searchForm.mobile" placeholder="请输入手机号" allowClear />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="loadUserData">查询</a-button>
          <a-button style="margin-left: 8px;" @click="resetSearch">重置</a-button>
        </a-form-item>
      </a-form>

      <div style="margin-top: 20px;">
        <a-table
          :columns="columns"
          :data-source="userData"
          :loading="loading"
          :pagination="pagination"
          rowKey="id"
          style="width: 100%"
          @change="handleTableChange">
          <span slot="tenant_name" slot-scope="text, record">
            <a-tag v-if="record.tenant">{{ record.tenant.name }}</a-tag>
            <span v-else>-</span>
          </span>
          <span slot="role_name" slot-scope="text, record">
            <a-tag v-if="record.role">{{ record.role.name }}</a-tag>
            <span v-else>-</span>
          </span>
          <span slot="status" slot-scope="status">
            <a-tag :color="status === 1 ? 'green' : 'red'">
              {{ status === 1 ? '启用' : '禁用' }}
            </a-tag>
          </span>
          <span slot="actions" slot-scope="text, record">
            <a-dropdown>
              <a class="ant-dropdown-link" @click="e => e.preventDefault()">
                操作 <a-icon type="down" />
              </a>
              <a-menu slot="overlay">
                <a-menu-item key="edit" @click="openEditDialog(record)">
                  <a-icon type="edit" />编辑
                </a-menu-item>
                <a-menu-item key="reset" @click="openResetPasswordDialog(record)">
                  <a-icon type="lock" />重置密码
                </a-menu-item>
                <a-menu-item key="delete" :disabled="record.id === currentUserId">
                  <a-tooltip v-if="record.id === currentUserId" title="不能删除自己" placement="left">
                    <span class="disabled-menu-item"><a-icon type="delete" />删除</span>
                  </a-tooltip>
                  <a-popconfirm
                    v-else
                    title="确定要删除这个用户吗？此操作不可恢复"
                    @confirm="deleteUser(record)"
                    okText="确定"
                    cancelText="取消"
                  >
                    <a-icon type="delete" />删除
                  </a-popconfirm>
                </a-menu-item>
              </a-menu>
            </a-dropdown>
          </span>
        </a-table>
      </div>

      <!-- 用户编辑对话框 -->
      <a-modal :title="dialogTitle" :visible="dialogVisible" @ok="saveUser" @cancel="closeDialog" width="500px">
        <a-form :form="userForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
          <a-form-item label="用户名" :validate-status="getUserFieldError('user_name') ? 'error' : ''" :help="getUserFieldError('user_name') || ''">
            <a-input 
              v-decorator="[
                'user_name',
                {
                  rules: [
                    { required: true, message: '请输入用户名' },
                    { min: 2, max: 20, message: '长度在 2 到 20 个字符' }
                  ]
                }
              ]"
              placeholder="请输入用户名" />
          </a-form-item>
          
          <!-- 只有超管可以选择组织，非超管不显示此字段（自动使用当前用户tenant_id） -->
          <a-form-item label="所属组织" v-if="isSuperAdmin">
            <a-select 
              v-decorator="[
                'tenant_id',
                { rules: [{ required: true, message: '请选择组织' }] }
              ]"
              placeholder="请选择组织"
              @change="handleDialogTenantChange">
              <a-select-option 
                v-for="tenant in tenants" 
                :key="tenant.id" 
                :value="tenant.id">
                {{ tenant.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
          
          <a-form-item label="手机号" :validate-status="getUserFieldError('mobile') ? 'error' : ''" :help="getUserFieldError('mobile') || ''">
            <a-input 
              v-decorator="[
                'mobile',
                {
                  rules: [
                    { required: true, message: '请输入手机号' },
                    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码' }
                  ]
                }
              ]"
              placeholder="请输入手机号" />
          </a-form-item>
          <a-form-item label="邮箱" :validate-status="getUserFieldError('user_mail') ? 'error' : ''" :help="getUserFieldError('user_mail') || ''">
            <a-input 
              v-decorator="[
                'user_mail',
                {
                  rules: [
                    { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: '请输入正确的邮箱地址' }
                  ]
                }
              ]"
              placeholder="请输入邮箱" />
          </a-form-item>
          
          <!-- 角色选择 -->
          <a-form-item label="角色" :validate-status="getUserFieldError('role_id') ? 'error' : ''" :help="getUserFieldError('role_id') || ''">
            <a-select 
              v-decorator="[
                'role_id',
                { rules: [{ required: true, message: '请选择角色' }] }
              ]"
              placeholder="请选择角色">
              <a-select-option 
                v-for="role in filteredRoles" 
                :key="role.id" 
                :value="role.id"
                :disabled="role.code <= loginUserRoleCode">
                {{ role.name }} (权限等级: {{ role.code }})
              </a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item v-if="!currentUser.id" label="密码" :validate-status="getUserFieldError('user_password') ? 'error' : ''" :help="getUserFieldError('user_password') || ''">
            <a-input-password 
              v-decorator="[
                'user_password',
                {
                  rules: [
                    { required: true, message: '请输入密码' },
                    { min: 6, message: '密码长度不能少于6位' },
                    { pattern: /^(?=.*[0-9])(?=.*[a-zA-Z])[0-9a-zA-Z!@#$%^&*]{6,}$/, message: '密码必须包含数字和英文' }
                  ]
                }
              ]"
              placeholder="请输入密码（需包含数字和英文）" />
          </a-form-item>
          <a-form-item v-if="!currentUser.id" label="确认密码" :validate-status="getUserFieldError('confirm_password') ? 'error' : ''" :help="getUserFieldError('confirm_password') || ''">
            <a-input-password 
              v-decorator="[
                'confirm_password',
                {
                  rules: [
                    { required: true, message: '请再次输入密码' }
                  ]
                }
              ]"
              placeholder="请再次输入密码" />
          </a-form-item>
          
          <a-form-item label="状态">
            <a-radio-group v-decorator="['status', { initialValue: 1 }]">
              <a-radio :value="1">启用</a-radio>
              <a-radio :value="0">禁用</a-radio>
            </a-radio-group>
          </a-form-item>
        </a-form>
      </a-modal>

      <!-- 重置密码对话框 -->
      <a-modal 
        title="重置用户密码" 
        :visible="resetPasswordDialogVisible" 
        @ok="resetUserPassword" 
        @cancel="() => { resetPasswordDialogVisible = false }" 
        width="400px">
        <a-form :form="resetPasswordForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
          <a-form-item label="用户">
            <span>{{ resetPasswordUser.user_name }}</span>
          </a-form-item>
          <a-form-item label="新密码" :validate-status="getResetPasswordFieldError('new_password') ? 'error' : ''" :help="getResetPasswordFieldError('new_password') || ''">
            <a-input-password 
              v-decorator="[
                'new_password',
                {
                  rules: [
                    { required: true, message: '请输入新密码' },
                    { min: 6, message: '密码长度不能少于6位' }
                  ]
                }
              ]"
              placeholder="请输入新密码" />
          </a-form-item>
          <a-form-item label="确认密码" :validate-status="getResetPasswordFieldError('confirm_password') ? 'error' : ''" :help="getResetPasswordFieldError('confirm_password') || ''">
            <a-input-password 
              v-decorator="[
                'confirm_password',
                {
                  rules: [
                    { required: true, message: '请再次输入密码' }
                  ]
                }
              ]"
              placeholder="请再次输入密码" />
          </a-form-item>
        </a-form>
      </a-modal>
    </a-card>
  </div>
</template>

<script>
export default {
  name: 'TenantUserManagement',
  data() {
    return {
      searchFormRef: this.$form.createForm(this),
      userForm: this.$form.createForm(this),
      resetPasswordForm: this.$form.createForm(this),
      userData: [],
      loading: false,
      searchForm: {
        user_name: '',
        mobile: '',
        tenant_id: undefined
      },
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '用户名', dataIndex: 'user_name', width: 120 },
        { title: '手机号', dataIndex: 'mobile', width: 130 },
        { title: '邮箱', dataIndex: 'user_mail', width: 180 },
        { title: '组织', dataIndex: 'tenant', width: 120, scopedSlots: { customRender: 'tenant_name' } },
        { title: '角色', dataIndex: 'role', width: 120, scopedSlots: { customRender: 'role_name' } },
        { title: '状态', dataIndex: 'status', width: 100, scopedSlots: { customRender: 'status' } },
        { title: '创建时间', dataIndex: 'created_at', width: 160 },
        { title: '操作', width: 250, scopedSlots: { customRender: 'actions' } }
      ],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['10', '20', '50', '100']
      },
      dialogVisible: false,
      dialogTitle: '',
      currentUser: {},
      resetPasswordDialogVisible: false,
      resetPasswordUser: {},
      tenants: [], // 所有组织列表（仅超管可见）
      roles: [], // 当前租户的角色列表
      // 登录用户的角色code值 - 直接在data中初始化
      loginUserRoleCode: (() => {
        try {
          const userInfo = JSON.parse(localStorage.getItem('aoxy-user') || '{}');
          return Number(userInfo.role?.code) || 0;
        } catch (e) {
          return 0;
        }
      })(),
      dialogSelectedTenantId: undefined // 对话框中选中的组织ID（超管用）
    }
  },
  computed: {
    // 判断是否为超级管理员 (role.code == 100)
    // 使用 Number() 转换确保类型一致
    isSuperAdmin() {
      return Number(this.loginUserRoleCode) === 100;
    },
    // 判断是否为租户管理员 (role.code == 200)
    isTenantAdmin() {
      return Number(this.loginUserRoleCode) === 200;
    },
    // 获取当前租户名称
    currentTenantName() {
      const userInfo = JSON.parse(localStorage.getItem('aoxy-user') || '{}');
      if (userInfo.tenant_id && this.tenants.length > 0) {
        const tenant = this.tenants.find(t => t.id === userInfo.tenant_id);
        return tenant ? tenant.name : '未知组织';
      }
      return '';
    },
    // 过滤可选角色：只显示code值大于登录用户角色的角色（权限更低）
    filteredRoles() {
      // 过滤掉权限等级小于等于当前用户的角色
      return this.roles.filter(role => role.code > this.loginUserRoleCode);
    },
    // 获取当前登录用户ID
    currentUserId() {
      const userInfo = JSON.parse(localStorage.getItem('aoxy-user') || '{}');
      return userInfo.id;
    }
  },
  async mounted() {
    // 只有超管才加载租户列表
    if (this.isSuperAdmin) {
      await this.loadTenants()
    }
    // 加载当前租户角色列表
    await this.loadRoles()
    // 加载用户数据
    await this.loadUserData()
  },
  methods: {
    // 加载角色列表 - 超管加载所有角色，租户用户加载当前租户角色
    async loadRoles() {
      try {
        let response;
        if (this.isSuperAdmin) {
          // 超管使用all接口获取所有角色
          response = await this.$api.get_all_roles();
        } else {
          // 租户用户使用current-tenant接口获取当前租户角色
          response = await this.$api.get_current_tenant_roles();
        }
        
        this.roles = response.data || []
      } catch (error) {
        console.error('加载角色列表失败:', error)
        this.$message.error('加载角色列表失败')
      }
    },
    
    // 加载所有组织（仅超管）
    async loadTenants() {
      try {
        const response = await this.$api.getTenantUsersTenants()
        this.tenants = response.data || []
      } catch (error) {
        console.error('加载组织数据失败:', error)
        this.$message.error('加载组织数据失败')
      }
    },
    
    // 处理组织选择变化
    handleTenantChange(value) {
      this.pagination.current = 1
      this.loadUserData()
    },
    
    // 过滤选项（用于show-search自动过滤）
    filterTenantOption(input, option) {
      // ant-design-vue的show-search会自动过滤，这里返回true表示所有选项都可以参与过滤
      // 实际过滤由a-select组件内置完成
      return true;
    },
    
    // 处理对话框中组织选择变化（超管用，更新角色下拉列表）
    handleDialogTenantChange(tenantId) {
      this.dialogSelectedTenantId = tenantId
      // 清空已选角色，因为组织变了角色列表也变了
      this.userForm.setFieldsValue({ role_id: undefined })
    },
    
    async loadUserData() {
      this.loading = true
      try {
        const params = {
          page_no: this.pagination.current,
          page_size: this.pagination.pageSize,
          ...this.searchForm
        }
            
        // 租户管理员：强制只查看自己组织的用户
        const userInfo = JSON.parse(localStorage.getItem('aoxy-user') || '{}');
        if (this.isTenantAdmin && userInfo.tenant_id) {
          params.tenant_id = userInfo.tenant_id;
        }
            
        const response = await this.$api.getTenantUsersList(params)
        this.userData = response.data || []
        // 使用API返回的分页信息
        if (response.page) {
          this.pagination.total = response.page.total || 0
          this.pagination.current = response.page.page_no || 1
          this.pagination.pageSize = response.page.page_size || 10
        } else {
          // 如果没有分页信息，使用数据长度
          this.pagination.total = this.userData.length
        }
      } catch (error) {
        console.error('加载用户数据失败:', error)
        this.$message.error('加载用户数据失败')
      } finally {
        this.loading = false
      }
    },
    
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadUserData()
    },
    
    resetSearch() {
      this.searchForm = {
        user_name: '',
        mobile: '',
        tenant_id: undefined
      }
      this.pagination.current = 1
      this.loadUserData()
    },
    
    openCreateDialog() {
      this.dialogTitle = '新增用户'
      this.currentUser = {}
      this.dialogSelectedTenantId = undefined
      this.dialogVisible = true
      this.userForm.resetFields()
    },
    
    openEditDialog(row) {
      this.dialogTitle = '编辑用户'
      this.currentUser = { ...row }
      this.dialogSelectedTenantId = row.tenant_id
      this.dialogVisible = true
      this.$nextTick(() => {
        this.userForm.setFieldsValue({
          user_name: row.user_name,
          mobile: row.mobile,
          user_mail: row.user_mail,
          tenant_id: row.tenant_id,
          role_id: row.role?.id || row.role_id,
          status: row.status || 1
        })
      })
    },
    
    openResetPasswordDialog(row) {
      this.resetPasswordUser = { ...row }
      this.resetPasswordDialogVisible = true
      this.resetPasswordForm.resetFields()
    },
    
    closeDialog() {
      this.dialogVisible = false
      this.userForm.resetFields()
    },
    
    getUserFieldError(field) {
      const errors = this.userForm.getFieldError(field)
      return errors ? errors[0] : ''
    },
    
    getResetPasswordFieldError(field) {
      const errors = this.resetPasswordForm.getFieldError(field)
      return errors ? errors[0] : ''
    },
    
    async saveUser() {
      try {
        const values = await this.userForm.validateFields()
        
        if (this.currentUser.id) {
          // 更新用户
          const updateData = {
            user_name: values.user_name,
            mobile: values.mobile,
            user_mail: values.user_mail
          }
          
          // 如果角色发生变化，则更新角色
          if (values.role_id && values.role_id !== (this.currentUser.role?.id || this.currentUser.role_id)) {
            updateData.role_id = values.role_id
          }
          
          // 超管可以修改用户的组织归属
          if (this.isSuperAdmin && values.tenant_id) {
            updateData.tenant_id = values.tenant_id
          }
          // 租户管理员不能修改用户的组织归属
          
          await this.$api.updateTenantUser(this.currentUser.id, updateData)
          this.$message.success('更新用户成功')
        } else {
          // 创建用户
          if (values.user_password !== values.confirm_password) {
            this.$message.error('两次输入的密码不一致')
            return
          }
          
          const createData = {
            user_name: values.user_name,
            mobile: values.mobile,
            user_mail: values.user_mail,
            user_password: values.user_password,
            role_id: values.role_id
          }
          
          // 超管从表单选择租户，租户管理员自动绑定当前租户
          if (this.isSuperAdmin && values.tenant_id) {
            createData.tenant_id = values.tenant_id
          } else if (this.isTenantAdmin) {
            // 租户管理员自动使用自己所在的租户
            const userInfo = JSON.parse(localStorage.getItem('aoxy-user') || '{}');
            if (userInfo.tenant_id) {
              createData.tenant_id = userInfo.tenant_id
            }
          }
          
          // 调用正确的API
          const response = await this.$api.createTenantUser(createData)
          
          if (response.code === 200) {
            this.$message.success('创建用户成功')
          } else {
            throw new Error(response.message || '创建用户失败')
          }
        }
        
        this.closeDialog()
        await this.loadUserData()
      } catch (error) {
        if (error.errorFields) {
          // 表单验证错误
          return
        }
        console.error('保存用户失败:', error)
        this.$message.error(error.message || '保存用户失败')
      }
    },
    
    async resetUserPassword() {
      try {
        const values = await this.resetPasswordForm.validateFields()
        
        if (values.new_password !== values.confirm_password) {
          this.$message.error('两次输入的密码不一致')
          return
        }
        
        await this.$api.resetUserPassword(this.resetPasswordUser.id, {
          new_password: values.new_password
        })
        
        this.$message.success('重置密码成功')
        this.resetPasswordDialogVisible = false
      } catch (error) {
        if (error.errorFields) {
          // 表单验证错误
          return
        }
        console.error('重置密码失败:', error)
        this.$message.error(error.message || '重置密码失败')
      }
    },
    
    async deleteUser(row) {
      try {
        this.$confirm({
          title: '确认删除',
          content: `确定要删除用户 "${row.user_name}" 吗？`,
          okText: '确定',
          cancelText: '取消',
          onOk: async () => {
            try {
              await this.$api.deleteTenantUser(row.id)
              this.$message.success('删除成功')
              await this.loadUserData()
            } catch (error) {
              console.error('删除用户失败:', error)
              this.$message.error('删除用户失败')
            }
          }
        })
      } catch (error) {
        console.error('删除用户失败:', error)
        this.$message.error('删除用户失败')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.tenant-user-management-container {
  ::v-deep .ant-card {
    min-height: calc(100vh - 100px);
  }
  
  ::v-deep .ant-form-inline .ant-form-item {
    margin-bottom: 24px;
  }
  
  ::v-deep .ant-table {
    .ant-table-thead > tr > th {
      background-color: #fafafa;
      font-weight: 500;
    }
    
    .ant-table-tbody > tr:hover > td {
      background-color: #f5f5f5;
    }
  }
  
  ::v-deep .ant-pagination {
    .ant-pagination-total-text {
      line-height: 32px;
    }
  }
  
  // 组织标签样式
  ::v-deep .ant-tag {
    margin-right: 4px;
  }
  
  // 操作按钮间距
  ::v-deep .ant-btn {
    margin-right: 4px;
    
    &:last-child {
      margin-right: 0;
    }
  }
  
  // 禁用菜单项样式
  .disabled-menu-item {
    color: rgba(0, 0, 0, 0.25);
    cursor: not-allowed;
  }
}
</style>